import { Controller } from "@hotwired/stimulus"

// Stimulus controller para tokenización de tarjetas en suscripciones MP.
//
// Diferencias clave con app/javascript/modules/payment_brick.js (pagos one-off):
// - Usa CardPayment Brick (no Payment Brick): solo tokeniza tarjeta, no procesa pago.
// - public_key es de Greatfood (no del partner) porque Greatfood es el merchant.
// - El backend recibe el card_token_id y crea preapproval via Subscriptions::Create.
//
// HTML esperado:
//   <div data-controller="bricks-card-subscription"
//        data-bricks-card-subscription-public-key-value="APP_USR-..."
//        data-bricks-card-subscription-amount-value="29990"
//        data-bricks-card-subscription-form-id-value="subscription-form">
//   </div>
export default class extends Controller {
  static values = {
    publicKey: String,
    amount: Number,
    formId: String,
    payerEmail: String,
    submitText: String
  }

  async connect() {
    // Turbo sirve un preview cacheado en navegación back/forward. Ese preview
    // tiene el HTML viejo del Brick (iframes residuales). Skip — el segundo
    // connect() con la página fresh sí monta.
    if (document.documentElement.hasAttribute("data-turbo-preview")) {
      return
    }

    if (typeof window.MercadoPago === "undefined") {
      console.error("[bricks-card-subscription] MercadoPago SDK not loaded. Check application.html.erb")
      return
    }

    if (!this.element.id) {
      console.error("[bricks-card-subscription] Container must have an id attribute")
      return
    }

    // Limpia residuo del cache de Turbo antes de montar de nuevo.
    this.element.innerHTML = ""

    console.log("[bricks-card-subscription] Initializing with amount:", this.hasAmountValue ? this.amountValue : "(none — card change)")

    try {
      const mp = new window.MercadoPago(this.publicKeyValue, { locale: "es-CL" })
      const bricksBuilder = mp.bricks()
      this.createPromise = this.renderBrick(bricksBuilder)
      await this.createPromise
    } catch (error) {
      console.error("[bricks-card-subscription] Failed to initialize:", error)
      this.showError("No pudimos cargar el formulario de pago. Recarga la página.")
    }
  }

  async disconnect() {
    // Espera a que termine create() antes de desmontar — evita "Field already unmounted".
    if (this.createPromise) {
      try { await this.createPromise } catch (_) {}
      this.createPromise = null
    }

    if (this.brickController) {
      try {
        this.brickController.unmount()
        console.log("[bricks-card-subscription] Brick unmounted")
      } catch (error) {
        console.warn("[bricks-card-subscription] Error during unmount:", error)
      }
      this.brickController = null
    }

    // Deja el container limpio para la próxima vez que se monte.
    this.element.innerHTML = ""
  }

  async renderBrick(bricksBuilder) {
    // El Brick `cardPayment` exige `amount`. En el cambio de tarjeta NO se cobra
    // (el PUT a /preapproval solo asocia la tarjeta), pero igual hay que pasarle
    // el monto del plan para que inicialice. Por eso, en ese contexto:
    //   - mostramos copy que aclara que no cobramos ahora, y
    //   - sobrescribimos el texto del botón a "Actualizar tarjeta" (submitText).
    const initialization = { payer: { email: this.payerEmailValue } }
    if (this.hasAmountValue) {
      initialization.amount = this.amountValue
    }

    const visual = { style: { theme: "bootstrap" } }
    if (this.hasSubmitTextValue) {
      visual.texts = { formSubmit: this.submitTextValue }
    }

    const settings = {
      initialization,
      customization: {
        visual,
        paymentMethods: {
          maxInstallments: 1
        }
      },
      callbacks: {
        onReady: () => {
          console.log("[bricks-card-subscription] Brick ready")
        },
        onSubmit: async (cardFormData) => {
          console.log("[bricks-card-subscription] onSubmit triggered")
          console.log("[bricks-card-subscription] cardFormData from Brick:", cardFormData)

          if (!cardFormData?.token) {
            console.error("[bricks-card-subscription] No token in cardFormData")
            return Promise.reject("No se generó el token de tarjeta")
          }

          const tokenInput = document.getElementById("card_token_id_input")
          if (!tokenInput) {
            console.error("[bricks-card-subscription] #card_token_id_input not found")
            return Promise.reject("Form mal configurado")
          }
          tokenInput.value = cardFormData.token

          const form = document.getElementById(this.formIdValue)
          if (!form) {
            console.error("[bricks-card-subscription] Form not found:", this.formIdValue)
            return Promise.reject("Form no encontrado")
          }

          // Debug: todos los params que se van a enviar al backend
          const formParams = Object.fromEntries(new FormData(form))
          console.log("[bricks-card-subscription] Form params to submit:", formParams)

          form.submit()

          return Promise.resolve()
        },
        onError: (error) => {
          console.error("[bricks-card-subscription] Brick error:", error)
        }
      }
    }

    this.brickController = await bricksBuilder.create(
      "cardPayment",
      this.element.id,
      settings
    )
  }

  showError(message) {
    this.element.innerHTML = `
      <div class="alert alert-danger" role="alert">
        ${message}
      </div>
    `
  }
}
